import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_SETTING_PC,
  DEFAULT_SETTING_REFLECTION,
  DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  SettingsResponsePC,
  SettingsResponseReflection,
  SettingsResponseWorkflowStopAfter,
} from "@pages/data-management/data-management-types";
import { FaroButton } from "@components/common/faro-button";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereToggleButtonGroup } from "@components/common/sphere-toggle-button-group";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { SdbProject } from "@custom-types/project-types";
import { ValueLabelItem } from "@custom-types/types";
import { CloudConnectionIcon, DeleteIcon, FolderSmallIcon } from "@faro-lotv/flat-ui";
import { useOnUploadFiles } from "@hooks/data-management/use-on-upload-any-files";
import { useMediaQueryList } from "@hooks/use-media-query";
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { getFileSizeForFileInfos, getPublishSettings, getTotalFileSizeOfFileInfos, setPublishSettings } from "@pages/data-management/data-management-utils";
import { PublishOptions } from "@pages/data-management/publish-options";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { colorConst, sphereColors } from "@styles/common-colors";
import { withEllipsis } from "@styles/common-styles";
import { bytesToSizeString } from "@utils/file-utils";
import { UploadSettings } from "@hooks/data-management/use-upload-multiple-scans";
import {
  AnyFilesInfo,
  isLsDataV2FilesInfo,
  isInvalidFileInfo,
} from "@pages/data-management/data-management-types";
import { getFallbackFolderName } from "@pages/data-management/import-data/import-data-utils";
import { NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { useSettingsApiClient } from "@api/use-settings-api-client";
import { DataManagementUploadElementType } from "@pages/data-management/data-management-types";
import { SettingsApiTypes } from "@stellar/settings-api-client";

interface Props {
  project: SdbProject,
  /** File infos constructed by useOnCheckFiles. */
  fileInfos: AnyFilesInfo[],
  /** Callback for setting the file infos. */
  setFileInfos: React.Dispatch<React.SetStateAction<AnyFilesInfo[]>>,
  /** Callback for setting the total file size, which is also shown in the upload page of the wizard. */
  setTotalFileSize: React.Dispatch<React.SetStateAction<number>>,
  /** Loading state. */
  isLoading: boolean;
  /** Callback from DataManagementDropzone for setting the loading state. */
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
}

type PublishType = "auto" | "staged";
const SphereToggleButtonGroupPublishType = SphereToggleButtonGroup<PublishType>;

/** Maps the DataManagement workflow type to the setting from SettingsAPI. */
const workflowTypeToStopAfterMapping: Record<PublishType, SettingsApiTypes.CaptureTreeWorkflowStopAfter> = {
  // "auto" means the workflow goes through the full process including "publishing".
  auto: "publish",
  // "staged" means the workflow stops after "registration", i.e. manual publishing.
  staged: "register",
};

/** Processing and publication options for the selected Blink scans. */
export function DataManagementOptions({
  project,
  fileInfos,
  setFileInfos,
  setTotalFileSize,
  isLoading,
  setIsLoading,
}: Props): JSX.Element {
  const settingsApiClient = useSettingsApiClient();
  const { isScreenXsAndSmall } = useMediaQueryList();
  const onUploadFiles = useOnUploadFiles(project);
  const { handleErrorWithToast } = useErrorContext();
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const [workflowType, setWorkflowType] = useState<PublishType>("auto");

  /* eslint-disable @typescript-eslint/naming-convention -- external package */
  const [settingPC, setSettingPC] = useState<SettingsResponsePC>({
    value: DEFAULT_SETTING_PC,
  } as SettingsResponsePC);
  const [settingReflection, setSettingReflection] = useState<SettingsResponseReflection>({
    value: DEFAULT_SETTING_REFLECTION,
  } as SettingsResponseReflection);
  const [settingWorkflowStopAfter, setSettingWorkflowStopAfter] = useState<SettingsResponseWorkflowStopAfter>({
    value: DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  } as SettingsResponseWorkflowStopAfter);
  /* eslint-enable */

  const isLoadingSettings = !settingPC.modifiedAt || !settingReflection.modifiedAt;

  const hasLsDataV2 = useMemo(() => {
    return fileInfos.some(((fileInfo) => isLsDataV2FilesInfo(fileInfo)));
  }, [fileInfos]);

  const hasInvalidData = useMemo(() => {
    return fileInfos.some(((fileInfo) => isInvalidFileInfo(fileInfo)));
  }, [fileInfos]);

  // The files containing metadata are not included this way, but that should be negligible.
  const sizesTotal: Map<AnyFilesInfo, number> = useMemo(() => {
    return getFileSizeForFileInfos(fileInfos);
  }, [fileInfos]);

  /**
   * Handles a change of the workflow type toggle button.
   * Updates the workflow type and the setting for stopping the workflow after registration or publishing.
   * @param workflowType The new workflow type.
   */
  const handleWorkflowTypeChange = useCallback((workflowType: PublishType) => {
    setWorkflowType(workflowType);
    setSettingWorkflowStopAfter((prev) => ({ ...prev,
      value: workflowTypeToStopAfterMapping[workflowType],
    }));
  }, [setSettingWorkflowStopAfter]);

  /**
   * Save the settings with a "last writer wins" approach, as specified by PM.
   * Shows an error toast if saving fails.
   * @returns True on success, false on error.
   */
  const applySettings = useCallback(() => {
    return setPublishSettings(project.id, settingsApiClient, handleErrorWithToast,
      { settingPC, settingReflection, settingWorkflowStopAfter }
    );
  }, [settingsApiClient, handleErrorWithToast, project.id, settingPC, settingReflection, settingWorkflowStopAfter]);

  /** Handles a click on the Import button to start the upload. */
  async function startUpload(): Promise<void> {
    setIsLoading(true);

    if (hasLsDataV2 && !await applySettings()) {
      setIsLoading(false);
      return;
    }

    try {
      const uploadSettings: UploadSettings = {
        workflowType,
        shouldGeneratePointCloud: settingPC.value,
        shouldApplyReflectionFilters: settingReflection.value,
      };

      const totalSize = getTotalFileSizeOfFileInfos(fileInfos);
      setTotalFileSize(totalSize);
      await onUploadFiles(fileInfos, uploadSettings);
    } finally {
      setFileInfos([]);
      setIsLoading(false);
    }
  }

  useEffect(() => {

    (async () => {
      const { settingPC, settingReflection, settingWorkflowStopAfter } = await getPublishSettings(project.id, settingsApiClient);
      setSettingPC(settingPC);
      setSettingReflection(settingReflection);
      // We don't care about the previous value, just set it from currently selected workflow type,
      // but it is still required to read it from SettingsApi as the `modifiedAt` is required here.
      settingWorkflowStopAfter.value = workflowTypeToStopAfterMapping[workflowType];
      setSettingWorkflowStopAfter(settingWorkflowStopAfter);
    })().catch(() => undefined);
  }, [settingsApiClient, project.id, workflowType]);

  // Properties of the workflow type toggle buttons.
  const workflowTypes: ValueLabelItem<PublishType>[] = [
    {
      value: "auto",
      label: "Auto-publish",
      icon: <CloudConnectionIcon/>,
      isDisabled: isLoading,
    },
    {
      value: "staged",
      label: "Staged publish",
      icon: <CloudConnectionIcon/>,
      isDisabled: isLoading,
    },
  ];

  const groupedFileInfos = useMemo(() => {
    // Group file infos by their type
    const groups: Record<DataManagementUploadElementType, {
      title: string,
      items: AnyFilesInfo[]
    }> = {
      elsScan: { title: "Blink data", items: [] },
      focusScan: { title: "Focus data", items: [] },
      orbisScan: { title: "Orbis data", items: [] },
      photogrammetry: { title: "Photogrammetry data", items: [] },
    };

    // Add all file infos to the corresponding group
    for (const info of fileInfos) {
      if (!isInvalidFileInfo(info)) {
        groups[info.type].items.push(info);
      }
    }

    // Only return groups that have items
    return Object.values(groups).filter((group) => group.items.length > 0);
  }, [fileInfos]);

  const listItems: JSX.Element[] = useMemo(() => {
    const items: JSX.Element[] = [];

    groupedFileInfos.forEach((group, groupIdx) => {
      // Add group header
      items.push(
        <ListItem
          key={`group-${groupIdx}`}
          sx={{
            marginTop: "25px",
            marginLeft: "0px",
            paddingBottom: "10px",
            fontWeight: "bold",
          }}
        >
          {group.title}
        </ListItem>
      );

      // Add items in this group
      group.items.forEach((fileInfo, itemIdx) => {
        const folderName: ReactNode = fileInfo.name
          ? <var title={fileInfo.name}>{fileInfo.name}</var>
          : getFallbackFolderName(fileInfo);
        const folderSize = <var>{bytesToSizeString(sizesTotal.get(fileInfo) || 0)}</var>;

        items.push(
          <ListItem
            key={`group-${groupIdx}-item-${itemIdx}`}
            secondaryAction={
              <FaroIconButton
                dataTestId="sa-options-remove-folder"
                isDisabled={isLoading}
                component={DeleteIcon}
                onClick={() => {
                  setFileInfos((prev) => prev.filter((info) => info !== fileInfo));
                }}
              />
            }
            sx={{
              paddingTop: "6px",
              paddingBottom: "6px",
              backgroundColor: sphereColors.pureWhite,
              borderBottom: `1px solid ${sphereColors.gray200}`,
            }}
          >
            <ListItemIcon>
              <FolderSmallIcon sx={{ color: sphereColors.gray800 }}/>
            </ListItemIcon>
            <ListItemText
              sx={{
                marginLeft: "-20px",
                "& span": {
                  ...withEllipsis,
                },
              }}
              primary={folderName}
            />
            <ListItemText
              sx={{
                marginLeft: "6px",
                marginRight: "6px",
                flexShrink: 0,
              }}
              primary={folderSize}
              primaryTypographyProps={{
                sx: {
                  textAlign: "right",
                  fontSize: "12px",
                },
              }}
            />
          </ListItem>
        );
      });
    });

    return items;
  }, [groupedFileInfos, sizesTotal, isLoading, setFileInfos]);


  return (
    <Stack sx={{
      marginLeft: isScreenXsAndSmall ? undefined : "25px",
      width:      isScreenXsAndSmall ? undefined : "400px",
      minWidth:   isScreenXsAndSmall ? undefined : "400px",
      marginTop:  isScreenXsAndSmall ? "20px" : undefined,
      padding:    isScreenXsAndSmall ? "20px" : undefined,
    }}>
      { hasLsDataV2 && (
        <Stack>
          <Box sx={{ fontSize: "20px" }}>
            Ready to bring your data into the cloud?
          </Box>

          {/* [TF-2028] Remove dev flag check once manual workflow is fully ready. */}
          { isDevModeEnabled && (
            // It looks like the backend for staged publish will take a bit longer than the PPC/reflection settings,
            // so we hide it as developer feature for now.
            <>
              <Box component="div" className={NO_TRANSLATE_CLASS}>
                <Box sx={{ marginTop: "25px", fontWeight: "bold", color: colorConst.devFeature }}>
                  Workflow Type
                </Box>
                <Box sx={{ marginTop: "10px", width: "100%" }}>
                  <SphereToggleButtonGroupPublishType
                    items={workflowTypes}
                    currentValue={workflowType}
                    handleChange={handleWorkflowTypeChange}
                    hasCheckIcon={true}
                    sxGroup={{ display: "flex" }}
                    sxButtons={{ width: "50%", maxWidth: "50%" }}
                  />
                </Box>
                <Box sx={{ marginTop: "10px", color: sphereColors.gray500 }}>
                  { workflowType === "auto" &&
                    <>
                      Automatically processes, registers, and publishes scans upon import, making them available immediately.
                    </>
                  }
                  { workflowType === "staged" &&
                    <>
                      Processes and registers the scans without publishing, allowing for further review and adjustments before
                      making them available.
                      <div style={{color: colorConst.devFeature}}>
                        Email and worker logic not fully adapted yet! (HBINF-2110, SMETA-1754, SMETA-1814, NRT-2156)
                      </div>
                    </>
                  }
                </Box>
              </Box>
              <Divider sx={{ marginTop: "20px" }} />
            </>
          )}

          { workflowType === "auto" && (
            <>
              <PublishOptions
                settingPC={settingPC}
                setSettingPC={setSettingPC}
                settingReflection={settingReflection}
                setSettingReflection={setSettingReflection}
              />
              <Divider sx={{ marginTop: "20px" }} />
            </>
          )}
        </Stack>
      )}

      <Box sx={{ marginTop: "10px" }}>
        <List
          dense={true}
          sx={{
            paddingTop: "0px",
            paddingBottom: "0px",
          }}
        >
          { listItems }
        </List>
      </Box>

      <Box sx={{
        width: "100%",
        height: "100%",
        marginTop: "40px",
        textAlign: "right",
        alignContent: "end",
      }}>
        <Stack direction="row" sx={{ display: "inline-flex" }}>
          <FaroTextButton
            isDisabled={isLoading}
            onClick={() => {
              setFileInfos([]);
            }}
          >
            Cancel
          </FaroTextButton>

          <FaroButton
            isLoading={isLoading}
            isDisabled={isLoading || isLoadingSettings || hasInvalidData}
            sx={{ marginLeft: "10px" }}
            onClick={() => {
              void startUpload();
            }}
          >
            Import
          </FaroButton>
        </Stack>
      </Box>
    </Stack>
  );
}
